/**
 * `useUnpublishedTracker(slug)` — reactive boolean: does this template have
 * UILM key changes that haven't been republished yet?
 *
 * Built on `useSyncExternalStore` so any component subscribed to the same
 * slug re-renders the moment the underlying flag flips, even across tabs
 * (via the native `storage` event) and within the same tab (via our custom
 * `uilm:unpublished:changed` event).
 */

import { useSyncExternalStore } from 'react';
import {
  hasUnpublished,
  UNPUBLISHED_CHANGED_EVENT,
} from '../lib/unpublished-tracker';

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(UNPUBLISHED_CHANGED_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(UNPUBLISHED_CHANGED_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

export function useUnpublishedTracker(slug: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => hasUnpublished(slug),
    () => false, // SSR fallback — no localStorage on the server
  );
}
